import { useEffect, useRef } from 'react';
import { useAuth } from '../lib/auth.js';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: GoogleInitConfig) => void;
          renderButton: (parent: HTMLElement, options: GoogleButtonOptions) => void;
          prompt: () => void;
        };
      };
    };
  }
}

type GoogleInitConfig = {
  client_id: string;
  callback: (response: { credential: string }) => void;
  hd?: string;
  ux_mode?: 'popup' | 'redirect';
  auto_select?: boolean;
};

type GoogleButtonOptions = {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  logo_alignment?: 'left' | 'center';
  width?: number;
};

const GSI_SCRIPT_URL = 'https://accounts.google.com/gsi/client';

/**
 * Google Identity Services sign-in button.
 *
 * The script is loaded once on first mount; the button renders into a div
 * we control. On credential delivery we hand it to AuthProvider.signIn,
 * which exchanges it for our session cookie at /api/auth/google.
 */
export function SignIn() {
  const buttonRef = useRef<HTMLDivElement>(null);
  const { signIn, state } = useAuth();

  useEffect(() => {
    if (!buttonRef.current) return;

    const clientId = import.meta.env['VITE_GOOGLE_CLIENT_ID'];
    if (!clientId) {
      // eslint-disable-next-line no-console
      console.error('VITE_GOOGLE_CLIENT_ID is not set');
      return;
    }

    let cancelled = false;
    const init = () => {
      if (cancelled || !buttonRef.current) return;
      if (!window.google) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (response) => {
          void signIn(response.credential);
        },
        hd: 'getvim.com',
        ux_mode: 'popup',
        auto_select: false,
      });
      window.google.accounts.id.renderButton(buttonRef.current, {
        type: 'standard',
        theme: 'filled_black',
        size: 'large',
        shape: 'pill',
        text: 'continue_with',
        logo_alignment: 'left',
        width: 280,
      });
    };

    if (window.google) {
      init();
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SCRIPT_URL}"]`);
    if (existing) {
      existing.addEventListener('load', init, { once: true });
      return () => {
        cancelled = true;
        existing.removeEventListener('load', init);
      };
    }

    const script = document.createElement('script');
    script.src = GSI_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', init, { once: true });
    document.head.appendChild(script);

    return () => {
      cancelled = true;
      script.removeEventListener('load', init);
    };
  }, [signIn]);

  const blockedReason = state.status === 'signed-out' ? state.reason : undefined;

  return (
    <div className="card" style={{ padding: 32, maxWidth: 480, margin: '60px auto', textAlign: 'center' }}>
      <div className="eyebrow" style={{ marginBottom: 16 }}>▌ Sign in</div>
      <h1 style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', margin: '0 0 12px' }}>
        Notion · Vim — From Zero
      </h1>
      <p style={{ color: 'var(--color-text-3)', fontSize: 13, lineHeight: 1.6, marginBottom: 24 }}>
        Sign in with your <strong style={{ color: 'var(--color-text)' }}>@getvim.com</strong> Google account to continue.
      </p>
      <div ref={buttonRef} style={{ display: 'inline-block' }} />
      {blockedReason === 'not_a_getvim_account' && (
        <p style={{ color: 'var(--color-danger)', fontSize: 12.5, marginTop: 18 }}>
          That Google account isn't a Vim account. Use your <strong>@getvim.com</strong> address.
        </p>
      )}
    </div>
  );
}
